import React, { useState, useEffect } from 'react';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api';

interface TelegramScenario {
  id: string;
  name: string;
  description: string | null;
  trigger: string;
  triggerData: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const TelegramScenarios: React.FC = () => {
  const [scenarios, setScenarios] = useState<TelegramScenario[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingScenario, setEditingScenario] = useState<TelegramScenario | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    trigger: 'message',
    triggerData: '',
    isActive: true
  });

  useEffect(() => {
    fetchScenarios();
  }, []);

  const fetchScenarios = async () => {
    try {
      setLoading(true);
      const data = await apiGet<TelegramScenario[]>('/telegram-scenarios');
      setScenarios(data);
    } catch (error) {
      console.error('Ошибка загрузки сценариев:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (scenario?: TelegramScenario) => {
    if (scenario) {
      setEditingScenario(scenario);
      setFormData({
        name: scenario.name,
        description: scenario.description || '',
        trigger: scenario.trigger,
        triggerData: scenario.triggerData ? JSON.stringify(JSON.parse(scenario.triggerData), null, 2) : '',
        isActive: scenario.isActive
      });
    } else {
      setEditingScenario(null);
      setFormData({
        name: '',
        description: '',
        trigger: 'message',
        triggerData: '',
        isActive: true
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingScenario(null);
    setFormData({
      name: '',
      description: '',
      trigger: 'message',
      triggerData: '',
      isActive: true
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        triggerData: formData.triggerData ? JSON.parse(formData.triggerData) : null
      };

      if (editingScenario) {
        await apiPut(`/telegram-scenarios/${editingScenario.id}`, payload);
      } else {
        await apiPost('/telegram-scenarios', payload);
      }

      await fetchScenarios();
      handleCloseModal();
    } catch (error: any) {
      console.error('Ошибка сохранения сценария:', error);
      alert(error.response?.data?.error || 'Ошибка сохранения сценария');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Вы уверены, что хотите удалить этот сценарий?')) {
      return;
    }

    try {
      await apiDelete(`/telegram-scenarios/${id}`);
      await fetchScenarios();
    } catch (error: any) {
      console.error('Ошибка удаления сценария:', error);
      alert(error.response?.data?.error || 'Ошибка удаления сценария');
    }
  };

  const getTriggerLabel = (trigger: string) => {
    const labels: { [key: string]: string } = {
      'message': 'Сообщение',
      'command': 'Команда',
      'callback': 'Callback',
      'event': 'Событие'
    };
    return labels[trigger] || trigger;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">📋 Сценарии Telegram бота</h1>
        <button
          onClick={() => handleOpenModal()}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          + Добавить сценарий
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Название
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Описание
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Триггер
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Статус
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Действия
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {scenarios.map((scenario) => (
              <tr key={scenario.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{scenario.name}</div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-500">{scenario.description || '-'}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                    {getTriggerLabel(scenario.trigger)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    scenario.isActive
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {scenario.isActive ? 'Активен' : 'Неактивен'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => handleOpenModal(scenario)}
                    className="text-blue-600 hover:text-blue-900 mr-4"
                  >
                    Редактировать
                  </button>
                  <button
                    onClick={() => handleDelete(scenario.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    Удалить
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {scenarios.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">Сценарии не найдены</p>
          </div>
        )}
      </div>

      {/* Модальное окно */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                {editingScenario ? 'Редактировать сценарий' : 'Создать сценарий'}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Название *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Описание
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Триггер *
                </label>
                <select
                  value={formData.trigger}
                  onChange={(e) => setFormData({ ...formData, trigger: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="message">Сообщение</option>
                  <option value="command">Команда</option>
                  <option value="callback">Callback</option>
                  <option value="event">Событие</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Данные триггера (JSON)
                </label>
                <textarea
                  value={formData.triggerData}
                  onChange={(e) => setFormData({ ...formData, triggerData: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  rows={5}
                  placeholder='{"command": "/start", "text": "привет"}'
                />
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Активен</span>
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Сохранить
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TelegramScenarios;

